// devReset — return the browser to a known state for THIS worktree.
//
// The problem it solves is stated in vite.config.ts: browser state is keyed by origin, so until
// every worktree pins its own port they share one bucket. Isolation stops NEW contamination; it
// does nothing for a browser that has been accumulating since before the ports were split, and
// nothing for a service worker already registered at scope "/" (docs/REVIEW_LOG.md 2026-09-06 F1).
// This is the one-shot cleanup for that.
//
// ABS-7. This destroys user-visible state, and demo data is treated as user-entered data (PO,
// 2026-09-04). So it hands back a backup of everything it is about to remove, and the caller is
// expected to save it before committing. Nothing here deletes without producing that first.

/** The Storage subset used here — `localStorage` satisfies it, and a test can supply a fake. */
export interface KeyValueStore {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
  clear(): void;
}

export interface DevResetTargets {
  serviceWorker?: Pick<ServiceWorkerContainer, "getRegistrations"> | undefined;
  cacheStorage?: Pick<CacheStorage, "keys" | "delete"> | undefined;
  local?: KeyValueStore | undefined;
  session?: KeyValueStore | undefined;
}

/** Named fields rather than a Record: under `noUncheckedIndexedAccess` an index signature makes
 *  every bucket possibly-undefined at the call site, for two keys that are always both present. */
export interface DevResetBackup {
  localStorage: Record<string, string>;
  sessionStorage: Record<string, string>;
}

export interface DevResetReport {
  serviceWorkers: number;
  caches: number;
  localKeys: number;
  sessionKeys: number;
  backup: DevResetBackup;
  backupBytes: number;
  failures: string[];
}

function snapshot(store: KeyValueStore | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  if (!store) return out;
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (k === null) continue;
    const v = store.getItem(k);
    if (v !== null) out[k] = v;
  }
  return out;
}

/** Everything that would be destroyed, read but not removed. Safe to call on its own. */
export function collectDevState(targets: DevResetTargets): {
  backup: DevResetBackup;
  backupBytes: number;
} {
  const backup: DevResetBackup = {
    localStorage: snapshot(targets.local),
    sessionStorage: snapshot(targets.session),
  };
  let backupBytes = 0;
  for (const bucket of [backup.localStorage, backup.sessionStorage]) {
    for (const [k, v] of Object.entries(bucket))
      backupBytes += k.length + v.length;
  }
  return { backup, backupBytes };
}

/** Unregister workers, drop caches, clear key/value stores. Returns what it removed plus the
 *  backup taken FIRST. Individual failures are collected, never thrown: a browser that refuses
 *  one step must still complete the others. */
export async function resetDevEnvironment(
  targets: DevResetTargets,
): Promise<DevResetReport> {
  const { backup, backupBytes } = collectDevState(targets);
  const failures: string[] = [];

  let serviceWorkers = 0;
  if (targets.serviceWorker) {
    try {
      const regs = await targets.serviceWorker.getRegistrations();
      for (const reg of regs) {
        try {
          if (await reg.unregister()) serviceWorkers++;
        } catch (e) {
          failures.push(`serviceWorker.unregister: ${(e as Error).message}`);
        }
      }
    } catch (e) {
      failures.push(`serviceWorker.getRegistrations: ${(e as Error).message}`);
    }
  }

  let caches = 0;
  if (targets.cacheStorage) {
    try {
      for (const name of await targets.cacheStorage.keys()) {
        try {
          if (await targets.cacheStorage.delete(name)) caches++;
        } catch (e) {
          failures.push(`caches.delete(${name}): ${(e as Error).message}`);
        }
      }
    } catch (e) {
      failures.push(`caches.keys: ${(e as Error).message}`);
    }
  }

  const localKeys = Object.keys(backup.localStorage).length;
  const sessionKeys = Object.keys(backup.sessionStorage).length;
  for (const [label, store] of [
    ["localStorage", targets.local],
    ["sessionStorage", targets.session],
  ] as const) {
    try {
      store?.clear();
    } catch (e) {
      failures.push(`${label}.clear: ${(e as Error).message}`);
    }
  }

  return {
    serviceWorkers,
    caches,
    localKeys,
    sessionKeys,
    backup,
    backupBytes,
    failures,
  };
}

/** The live browser's targets. Each is optional because jsdom and older browsers omit some. */
export function browserTargets(): DevResetTargets {
  return {
    serviceWorker:
      typeof navigator !== "undefined" && "serviceWorker" in navigator
        ? navigator.serviceWorker
        : undefined,
    cacheStorage: typeof caches !== "undefined" ? caches : undefined,
    local: typeof localStorage !== "undefined" ? localStorage : undefined,
    session: typeof sessionStorage !== "undefined" ? sessionStorage : undefined,
  };
}
